import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Receipt, Eye, Loader2, Search, Filter } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
  }).format(amount || 0);
};

const SalesPage = () => {
  const { api, organization } = useAuth();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSale, setSelectedSale] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const loadData = async () => {
    try {
      let url = '/pos/sales?limit=100';
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      const response = await api.get(url);
      setSales(response.data);
    } catch (error) {
      toast.error('Failed to load sales');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [api, statusFilter]);

  const filteredSales = sales.filter(s => {
    if (!searchQuery) return true;
    return s.receipt_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
           (s.cashier_name && s.cashier_name.toLowerCase().includes(searchQuery.toLowerCase()));
  });

  const viewSaleDetails = (sale) => {
    setSelectedSale(sale);
    setShowDetails(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-500/10 text-green-600 border-green-500/20';
      case 'void': return 'bg-red-500/10 text-red-600 border-red-500/20';
      case 'refund': return 'bg-orange-500/10 text-orange-600 border-orange-500/20';
      default: return 'bg-gray-500/10 text-gray-600 border-gray-500/20';
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="sales-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div>
          <h1 className="text-2xl font-bold">Sales History</h1>
          <p className="text-muted-foreground">View all transactions</p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search receipt #..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-11 w-[200px]"
              data-testid="sales-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[130px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="void">Voided</SelectItem>
              <SelectItem value="refund">Refunded</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Sales List */}
      <Card>
        <CardContent className="p-0">
          <ScrollArea className="h-[calc(100vh-250px)]">
            {filteredSales.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Receipt className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No sales found</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filteredSales.map(sale => (
                  <div 
                    key={sale.id}
                    className="p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => viewSaleDetails(sale)}
                    data-testid={`sale-row-${sale.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{sale.receipt_number}</span>
                          <Badge variant="outline" className={getStatusColor(sale.status)}>
                            {sale.status.toUpperCase()}
                          </Badge>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {new Date(sale.created_at).toLocaleString()}
                          {sale.cashier_name && <span> • {sale.cashier_name}</span>}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          {sale.items?.length || 0} item(s) • {sale.payment_method.replace('_', ' ').toUpperCase()}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-xl font-bold ${sale.status === 'refund' ? 'text-orange-600' : ''}`}>
                          {sale.status === 'refund' ? '-' : ''}{formatCurrency(Math.abs(sale.grand_total))}
                        </div>
                        <Button variant="ghost" size="sm" className="mt-1">
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Sale Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Sale Details</DialogTitle>
          </DialogHeader>
          
          {selectedSale && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-muted">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">{selectedSale.receipt_number}</span>
                  <Badge variant="outline" className={getStatusColor(selectedSale.status)}>
                    {selectedSale.status.toUpperCase()}
                  </Badge>
                </div>
                <div className="text-sm text-muted-foreground">
                  {new Date(selectedSale.created_at).toLocaleString()}
                </div>
                <div className="text-sm text-muted-foreground">
                  Cashier: {selectedSale.cashier_name}
                </div>
              </div>

              {/* Items */}
              <div className="space-y-2">
                <h4 className="font-medium">Items</h4>
                {selectedSale.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm p-2 rounded bg-muted/50">
                    <div>
                      <div>{item.product_name}</div>
                      <div className="text-muted-foreground">
                        {item.quantity} × {formatCurrency(item.unit_price)}
                      </div>
                    </div>
                    <div className="font-medium">{formatCurrency(item.line_total)}</div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="space-y-2 pt-4 border-t border-border">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>{formatCurrency(selectedSale.subtotal)}</span>
                </div>
                {selectedSale.discount_total > 0 && (
                  <div className="flex justify-between text-sm text-destructive">
                    <span>Discount</span>
                    <span>-{formatCurrency(selectedSale.discount_total)}</span>
                  </div>
                )}
                {organization?.vat_mode === 'vat_registered' && (
                  <div className="flex justify-between text-sm">
                    <span>VAT</span>
                    <span>{formatCurrency(selectedSale.vat_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-border">
                  <span>Total</span>
                  <span>{formatCurrency(selectedSale.grand_total)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Tendered</span>
                  <span>{formatCurrency(selectedSale.tendered_amount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Change</span>
                  <span>{formatCurrency(selectedSale.change_amount)}</span>
                </div>
              </div>

              {/* Payment Info */}
              <div className="p-3 rounded-lg bg-muted">
                <div className="text-sm">
                  <span className="text-muted-foreground">Payment Method: </span>
                  <span className="font-medium uppercase">{selectedSale.payment_method.replace('_', ' ')}</span>
                </div>
                {selectedSale.payment_reference && (
                  <div className="text-sm">
                    <span className="text-muted-foreground">Reference: </span>
                    <span className="font-medium">{selectedSale.payment_reference}</span>
                  </div>
                )}
              </div>

              {/* Void/Refund Info */}
              {selectedSale.status === 'void' && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                  <div className="text-sm font-medium text-red-600">Voided</div>
                  {selectedSale.void_reason && (
                    <div className="text-sm text-red-600/80">Reason: {selectedSale.void_reason}</div>
                  )}
                </div>
              )}
              {selectedSale.status === 'refund' && (
                <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
                  <div className="text-sm font-medium text-orange-600">Refunded</div>
                  {selectedSale.refund_reason && (
                    <div className="text-sm text-orange-600/80">Reason: {selectedSale.refund_reason}</div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesPage;
